/**
 * Switching project at the top takes the desktop app to that project's folder (docs/refactor/53):
 * Trabalho shows its files, as opening it would. The folder is the one remembered for it, else one
 * found on this computer by its git remote; with neither, the app asks where it is (the Git
 * picker's "Onde está este projeto?", which can also download it). The browser has no folders.
 */

import type { QueryClient } from '@tanstack/react-query'
import { isDesktop, projectAdd, type DesktopProject } from './desktop'
import { setProjectExpanded, upsertProject, cachedProjects } from './openProjects'
import { findFoldersHere, folderFor } from './repoFolders'
import type { RepositoryLink } from './types'

/** Asks the Git picker to say "where is this project?" — detail: the repository's id. */
export const ASK_FOLDER_EVENT = 'devpulse:ask-folder'

/** A project put at the top of Trabalho, opened, at once — the desktop's list stays the truth. */
export function showOpenProject(queryClient: QueryClient, project: DesktopProject) {
  setProjectExpanded(project.path, true)
  queryClient.setQueryData<DesktopProject[]>(['open-projects'], (list) => upsertProject(list ?? cachedProjects(), project))
}

/** Takes the app to the repository's folder. 'missing' when it is not on this computer (and the person was asked). */
export async function followRepository(repository: RepositoryLink, queryClient: QueryClient): Promise<'here' | 'missing' | 'web'> {
  // The free project lives in the app's own folder, made on first use (docs/refactor/49).
  if (!isDesktop() || repository.isDefault) return 'web'
  if (!folderFor(repository.name)) await findFoldersHere([repository], true)
  const folder = folderFor(repository.name)
  if (!folder) {
    window.dispatchEvent(new CustomEvent(ASK_FOLDER_EVENT, { detail: repository.id }))
    return 'missing'
  }
  const project = await projectAdd(folder).catch(() => null)
  if (project) showOpenProject(queryClient, project)
  return 'here'
}
